/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable @typescript-eslint/no-unused-vars, react/no-find-dom-node */
import { IDictionary } from '@msdyn365-commerce/retail-proxy';
import classnames from 'classnames';
import * as React from 'react';
import ReactDOM from 'react-dom';
import { IGridSettings, IImagePlaceholderConfig, IImageSettings } from '../../..';
import { IAny, IGeneric, IImageData, IImageDimension, IPartnerThemeSettings, IViewPort } from '../../../interfaces';
import { EditableField, FieldType } from '../../../utilities/editable-fields/editable-field';
import { isDefaultPlaceholderFeatureOn, isEditorialMode } from '../../../utilities/editable-fields/utils';
import { getImageResizeUrl, getLargestGridSettingViewportName, getSourceData } from '../source-builder';
import { constructViewportInformationForImage } from '../image-helper';
import { isAbsoluteUrl } from '../../../utilities/helper';

export const placeholderImageUrl = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAEALAAAAAABAAEAAAIBTAA7';

export type Viewport = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type PictureLoadFailureBehavior = 'default' | 'hide' | 'empty';
const svgPreviewImage = 'svg.';
// query string parameter
const fileName = 'fileName';
// pathname
const searchPath = 'search';

export interface INativeImageState {
    loadFailed: boolean;
    src: string;
    loaded?: boolean;
}

export interface INativeImageProps extends IImageData {
    gridSettings: IGridSettings;
    imageSettings?: IImageSettings;
    pictureAttributes?: {
        className?: string;
        [attribute: string]: string | undefined;
    };
    className?: string;
    loadFailureBehavior?: PictureLoadFailureBehavior;
    parentTagToRemove?: string;
    shouldHideOnFailure?: boolean;
    onWillRender?: (state: INativeImageState) => void;
    imageFallbackOptimize?: boolean;
    bypassHideOnFailure?: boolean;
    quality?: number;
    [attribute: string]: unknown;
    preserveImageSpace?: boolean;
}

export const shouldUseDefaultSVGPlaceholder = (props: INativeImageProps) => {
    const requestContext = props.editProps && props.editProps.requestContext;
    // if default image placeholder feature is diabled, we dont use SVG placeholder.
    return isEditorialMode(requestContext) && isDefaultPlaceholderFeatureOn(requestContext);
};

/**
 * Image
 */
export class NativeImage extends React.PureComponent<INativeImageProps, INativeImageState> {
    private shouldHideOnFailure: boolean;

    constructor(props: INativeImageProps) {
        super(props);

        this.shouldHideOnFailure = this.props.shouldHideOnFailure ?? false;

        this.state = {
            loaded: false,
            loadFailed: false,
            src: this.getSrcImage(props) || ''
        };
        // if we are in editorial mode(WYSIWYG), dynamically load svg chunk and render placeholder SVG for empty images
        if (isEditorialMode(props.editProps?.requestContext)) {
            void import('../../../utilities/svg-loader').then(svg => {
                let stateSrc = this.getSrcImage(props);
                if (!props.src && shouldUseDefaultSVGPlaceholder(props)) {
                    stateSrc = `${svgPreviewImage}${svg.DEFAULT_PLACEHOLDER}`;
                }
                this.setState(prevState => ({ ...prevState, src: stateSrc || '' }));
            });
        }
    }

    public componentDidUpdate(prevProps: INativeImageProps): void {
        if (this.props.src !== prevProps.src || this.props.fallBackSrc !== prevProps.fallBackSrc) {
            this.setState({ loadFailed: false, src: this.props.src || '' });
        }
    }
    public componentDidMount(): void {
        // onError will sometimes fire before the react events get hooked up. So we
        // cannot rely on it for determining when image load fails. Instead we also have
        // to find the img that the Image component uses> If its complete, but naturalWidth
        // is zero that means load failed
        const node = ReactDOM.findDOMNode(this) as HTMLElement;

        if (node && node.querySelector) {
            const child = node.querySelector('img');
            if (child && child.complete && child.naturalWidth === 0) {
                if ((this.props.fallBackSrc && this.props.fallBackSrc === this.state.src) || !this.props.fallBackSrc) {
                    this.setState({ loadFailed: true });
                } else {
                    this.setState({ src: this.props.fallBackSrc ? this.props.fallBackSrc : this.props.src || '' });
                }
                return;
            }
            if (child && child.complete && child.naturalWidth > 1) {
                this.setState({ loaded: true });
            }
        }
    }

    public render(): JSX.Element | null {
        if (this.props.onWillRender) {
            this.props.onWillRender(this.state);
        }

        const { loadFailureBehavior, pictureAttributes = {}, gridSettings, fallBackSrc, ...imageAttributes } = this.props;
        if (this.state.loadFailed || !this.state.src) {
            if (loadFailureBehavior === 'hide') {
                return null;
            } else if (loadFailureBehavior === 'empty') {
                return <div className={classnames('msc-empty_image', imageAttributes.className)} />;
            }
        }
        const loaded = this.state.loaded;
        const imageSettings = this.props.imageSettings || imageAttributes.imageSettings;
        const largestGridSettingViewportName = getLargestGridSettingViewportName(gridSettings);

        // Lazyload by default if nothing is specified
        const lazyLoad = !imageSettings || !imageSettings.disableLazyLoad;

        // render placeholder if its using SVG placeholder image
        if (this.isUsingSVGPlaceholder(this.state.src, this.props)) {
            return this.defaultImage(largestGridSettingViewportName, imageAttributes, lazyLoad, imageSettings);
        }

        const sourceTags: (JSX.Element | undefined)[] =
            imageSettings && imageSettings.viewports
                ? Object.keys(imageSettings.viewports)
                      .map(vp =>
                          this.getSourceTag(vp as Viewport, imageSettings, largestGridSettingViewportName, lazyLoad, imageAttributes)
                      )
                      .filter(Boolean) || []
                : [];

        sourceTags.push(
            this.defaultImage(largestGridSettingViewportName, imageAttributes, lazyLoad, imageSettings, loaded, loadFailureBehavior)
        );
        return <picture {...pictureAttributes}>{sourceTags}</picture>;
    }

    // eslint-disable-next-line
    private getSVGConfig = (attributes: IDictionary<any>, largestViewport?: IImageDimension) => {
        // when add module from tools, the viewPort contains height/width instead of h/w.
        const SVGHeight = (largestViewport && largestViewport.h) || attributes.height;
        const SVGWidth = (largestViewport && largestViewport.w) || attributes.width;
        return {
            ...(SVGHeight && { height: SVGHeight }),
            ...(SVGWidth && { width: SVGWidth }),
            ...(this.getPlaceholderAspectRatio(this.state.src) && {
                preserveAspectRatio: this.getPlaceholderAspectRatio(this.state.src)
            })
        };
    };

    // eslint-disable-next-line
    private getSrcImage = (props: INativeImageProps) => {
        let stateSrc: string | undefined = '';
        // if no src indicated and we are rendering in site builder context.
        if (props.src && props.fallBackSrc && isAbsoluteUrl(props.src)) {
            const urlSrc = new URL(props.src);
            const urlFallBack = new URL(props.fallBackSrc);
            const paramsSrc = new URLSearchParams(urlSrc.search);
            const paramsFallBack = new URLSearchParams(urlFallBack.search);
            // when both src and fallBackSrc has fileName parameter
            if (props.src.includes(searchPath) && paramsFallBack.has(fileName) && paramsSrc.has(fileName)) {
                // append fileName parameter value for fallBack URL to  src URL
                stateSrc = `${props.src}&fallback=${this.convertToMultipleExtensions(paramsFallBack.get(fileName)!)}`;
            } else {
                stateSrc = props.src;
            }
        } else {
            stateSrc = props.src;
        }
        return stateSrc;
    };

    /**
     * Converts a given string representing a file path with an extension to multiple formats.
     * @param inputString The input string representing the file path with extension.
     * @returns A string containing comma-separated paths for each supported format.
     */
    private convertToMultipleExtensions = (inputString: string): string => {
        const formats = ['jpg', 'png', 'webp'];

        const baseParts = inputString.split('.', 1);
        if (baseParts.length > 0) {
            const [basePath] = baseParts;

            return formats.map(ext => `${basePath}.${ext}`).join(',');
        }
        return inputString;
    };

    // eslint-disable-next-line complexity
    private defaultImage = (
        largestGridSettingViewportName: Viewport,
        imageAttributes: IGeneric<IAny>,
        lazyLoad: boolean,
        inputImageSettings?: IImageSettings,
        loaded?: boolean,
        loadFailureBehavior?: string
    ) => {
        const {
            src,
            className,
            image,
            imageSettings,
            binaryHash,
            altText,
            quality,
            focalRegion,
            cropRegions,
            description,
            fileName,
            fileSize,
            imageStyle,
            isLocalizable,
            isDecorativeImage,
            orientation,
            _links,
            ...attributes
        } = imageAttributes;

        const cssClassName = this.props.className || '';
        const filteredClassName = lazyLoad ? (cssClassName ? `${cssClassName} lazyload` : 'lazyload') : cssClassName;
        let viewports = inputImageSettings && inputImageSettings.viewports;

        // if in editor mode and default image is enabled. we add viewport when there is no viewPort.
        if (
            (!viewports || Object.keys(viewports).length === 0) &&
            this.isUsingSVGPlaceholder(this.state.src, this.props) &&
            this.props.editProps &&
            process.env.CURRENT_ENVIRONMENT === 'web' &&
            window
        ) {
            const editProps = this.props.editProps;
            const imagePropertyName = editProps.imagePropertyName ? editProps.imagePropertyName : undefined;
            const themeSettings: IPartnerThemeSettings | undefined =
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (window as any)._msdyn365.authoringHelper && (window as any)._msdyn365.authoringHelper.themeSettings;
            viewports = constructViewportInformationForImage(
                editProps.moduleType,
                editProps.layout,
                imagePropertyName,
                themeSettings
            ) as IViewPort;
        }

        const largestViewport = viewports && viewports[largestGridSettingViewportName];

        // we fall back to width and height property when no viewPort found.
        if (largestViewport && Object.keys(largestViewport).length) {
            delete attributes.width;
            delete attributes.height;
        }
        let placeholderConfig: IImagePlaceholderConfig | undefined;

        if (this.isUsingSVGPlaceholder(this.state.src, this.props) && this.props.editProps) {
            placeholderConfig = {
                name: this.getPlaceholderName(this.state.src)
            };
            const SVGConfig = this.getSVGConfig(attributes, largestViewport);
            placeholderConfig.svgConfig = SVGConfig;
            placeholderConfig.imagePropertyName = this.props.editProps.imagePropertyName;
            placeholderConfig.moduleId = this.props.editProps.moduleId;
        }

        const urlSrc = getImageResizeUrl(
            this.state.src,
            largestViewport,
            inputImageSettings,
            undefined,
            imageAttributes?.externalProvider,
            imageAttributes?.requestContext?.apiSettings || imageAttributes?.editProps?.requestContext?.apiSettings
        );

        const shouldBeVisibleIfFallbackNotSpecified =
            this.shouldHideOnFailure || (this.props.fallBackSrc && !this.props.bypassHideOnFailure);
        attributes.imageFallbackOptimize = this.props.imageFallbackOptimize && this.props.imageSettings?.disableLazyLoad;
        const shouldBeVisible = loaded || this.state.loadFailed || !shouldBeVisibleIfFallbackNotSpecified;
        const hiddenStyle = shouldBeVisible ? {} : { visibility: 'hidden' };
        const parentTagToRemove = this.props.parentTagToRemove;

        return (
            <EditableField
                type={FieldType.Image}
                editPropKey={this.props.editProps && this.props.editProps.key}
                requestContext={this.props.editProps && this.props.editProps.requestContext}
                fieldProps={{
                    ...(hiddenStyle && { style: hiddenStyle }),
                    ...(this.props.fallBackSrc && { fallbacksrc: this.props.fallBackSrc }),
                    alt: this.props.altText,
                    ...(lazyLoad ? { src: placeholderImageUrl, 'data-src': urlSrc } : { src: urlSrc }),
                    ...attributes,
                    key: 'default-image',
                    className: filteredClassName,
                    onError: this.imageFailed,
                    onLoad: this.imageLoaded,
                    additionalProperties: this.props.additionalProperties,
                    ...(placeholderConfig && { placeholderConfig: placeholderConfig }),
                    ...(loadFailureBehavior && { loadFailureBehavior: loadFailureBehavior }),
                    ...(parentTagToRemove && { parentTagToRemove: parentTagToRemove })
                }}
            />
        );
    };

    private imageFailed = (...args: unknown[]) => {
        if ((this.props.fallBackSrc && this.props.fallBackSrc === this.state.src) || !this.props.fallBackSrc) {
            this.setState({ loadFailed: true });
        } else {
            this.setState({ src: this.props.fallBackSrc ? this.props.fallBackSrc : this.props.src || '' });
        }
        if (typeof this.props.onError === 'function') {
            this.props.onError(...args);
        }
    };

    private imageLoaded = (...args: unknown[]) => {
        const shouldBeVisibleIfFallbackNotSpecified = !this.shouldHideOnFailure && !this.props.fallBackSrc;
        if (shouldBeVisibleIfFallbackNotSpecified) {
            return;
        }

        const node = ReactDOM.findDOMNode(this) as HTMLElement;
        if (node && node.querySelector) {
            const child = node.querySelector('img');
            if (child && child.complete && child.naturalWidth > 1) {
                this.setState({ loaded: true });
            }
        }
    };

    private getSourceTag = (
        viewport: Viewport,
        imageSettings: IImageSettings,
        largestGridSettingViewportName: Viewport,
        lazyLoad: boolean,
        imageAttributes: IGeneric<IAny>
    ) => {
        const srcData = getSourceData(
            this.state.src,
            this.props.gridSettings,
            this.props,
            imageSettings,
            viewport,
            largestGridSettingViewportName,
            imageAttributes
        );
        const imageSetting = imageSettings.viewports[viewport];
        const style = {
            height: imageSetting?.h,
            width: imageSetting?.w
        };
        if (!srcData) {
            return;
        }
        return (
            <source
                key={viewport}
                {...(lazyLoad ? { 'data-srcset': srcData.srcSet } : { srcSet: srcData.srcSet })}
                {...(srcData.media ? { media: srcData.media } : {})}
                {...(this.props.preserveImageSpace && style)}
            />
        );
    };

    private isUsingSVGPlaceholder = (src: string, props: INativeImageProps) => {
        if (!src) {
            return false;
        }
        return src.startsWith(svgPreviewImage) && shouldUseDefaultSVGPlaceholder(props);
    };
    private getPlaceholderName = (src: string) => {
        const configs = src.split('.');
        if (configs.length >= 2) {
            return configs[1];
        }
        return '';
    };

    private getPlaceholderAspectRatio = (src: string) => {
        const configs = src.split('.');
        if (configs.length >= 3) {
            return configs[2];
        }
        return '';
    };
}
